// AR Aging widget — Shannon's money-outstanding picture (Ship 2, Aug 25 2026).
//
// Telemetry: her measured loop is finance→deal→finance (555 + 531 hops/60d),
// so the biggest balances are deal links. Balance = the derived
// `post_build_balance_remaining` (cleared-money truth). Aging uses the SAME
// anchor as the AR Aging tab this widget opens (`final_invoice_sent_date ??
// build_completed_date`, falling back to stage entry) so the bucket colors
// here match the escalation colors there. Counts only deals with an open
// balance — the attention sidebar's broader "receivables to work" count
// includes not-yet-invoiced deals and will read higher by design.
// Registry-gated to finance/admin/sales_director.
//
// Oct 16, 2026: each of the three rows carries the deal's newest text (the same
// strip as the Supplements board) and opens the reply drawer, so a homeowner's
// "check is in the mail" is read and answered from the home page.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { daysSince } from "../../../lib/dealAge";
import { logCardClick } from "../../../lib/uiEvents";
import type { TeamMember } from "../../../lib/types";
import { fetchCommsMap } from "../../../lib/fetchCommsMap";
import { withOptimisticOutbound, type CommsMap } from "../../../lib/commsStrip";
import CommsStrip from "../../comms/CommsStrip";
import DealCommsDrawer from "../../comms/DealCommsDrawer";

interface ArRow {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  homeowner_email: string | null;
  claim_number: string | null;
  stage: string | null;
  stage_entered_at: string | null;
  final_invoice_sent_date: string | null;
  build_completed_date: string | null;
  post_build_balance_remaining: number | null;
}

const fmtUSD0 = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtK = (n: number) => (n >= 1000 ? `$${Math.round(n / 1000)}K` : fmtUSD0(n));

const BUCKETS = [
  { label: "0–13d", max: 14, color: "#cbd5e1" },
  { label: "14–27", max: 28, color: "#b3bfce" },
  { label: "28–44", max: 45, color: "#f5c063" },
  { label: "45–59", max: 60, color: "#f0975a" },
  { label: "60d+", max: Infinity, color: "#e5484d" },
];

// Same aging anchor as the AR Aging tab this widget deep-links to.
const ageDays = (r: ArRow): number =>
  daysSince(r.final_invoice_sent_date ?? r.build_completed_date ?? r.stage_entered_at) ?? 0;

export default function ArWidget() {
  const [rows, setRows] = useState<ArRow[] | null>(null);
  // Newest text per listed deal (null = read failed → no strips) + the open thread.
  const [commsMap, setCommsMap] = useState<CommsMap | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const closeComms = useCallback(() => setOpenId(null), []);
  const [members, setMembers] = useState<Record<string, TeamMember> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("deals")
        .select(
          "id,homeowner_name,homeowner_phone,homeowner_email,claim_number,stage,stage_entered_at,final_invoice_sent_date,build_completed_date,post_build_balance_remaining",
        )
        .in("stage", ["collecting", "delinquent"])
        .gt("post_build_balance_remaining", 0);
      if (cancelled) return;
      if (!error) setRows((data as ArRow[]) ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(() => {
    if (!rows) return null;
    const total = rows.reduce((s, r) => s + Number(r.post_build_balance_remaining ?? 0), 0);
    const buckets = BUCKETS.map((b) => ({ ...b, amt: 0, n: 0 }));
    for (const r of rows) {
      const d = ageDays(r);
      const b = buckets.find((x) => d < x.max)!;
      b.amt += Number(r.post_build_balance_remaining ?? 0);
      b.n += 1;
    }
    const top = [...rows]
      .sort(
        (a, b) => Number(b.post_build_balance_remaining ?? 0) - Number(a.post_build_balance_remaining ?? 0),
      )
      .slice(0, 3);
    return { total, buckets, top };
  }, [rows]);

  // Strips for the three listed deals only.
  const topIds = view ? view.top.map((r) => r.id).join(",") : "";
  useEffect(() => {
    if (!topIds) return;
    let cancelled = false;
    fetchCommsMap(topIds.split(",")).then((m) => {
      if (!cancelled && m) setCommsMap(m);
    });
    return () => {
      cancelled = true;
    };
  }, [topIds]);

  // Team names for the thread, loaded once the first drawer opens.
  useEffect(() => {
    if (!openId || members !== null) return;
    let cancelled = false;
    supabase
      .from("team_roster")
      .select("*")
      .eq("is_active", true)
      .then(({ data }) => {
        if (cancelled) return;
        const byId: Record<string, TeamMember> = {};
        (data ?? []).forEach((x) => { byId[x.id] = x as TeamMember; });
        setMembers(byId);
      });
    return () => { cancelled = true; };
  }, [openId, members]);

  const openRow = openId && view ? view.top.find((r) => r.id === openId) ?? null : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>💰</span>
        <span className="font-bold text-sm text-bardin-ink">AR Aging</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {view ? `${rows!.length} open balances` : "…"}
        </span>
      </div>

      {!view ? (
        <div className="space-y-2 mt-2">
          <div className="h-7 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-9 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : (
        <>
          <div className="text-2xl font-extrabold text-bardin-ink leading-tight">{fmtUSD0(view.total)}</div>
          <div className="text-[11px] text-slate-500 font-semibold mb-2">
            outstanding · collecting + delinquent · aged from invoice/build
          </div>

          <div className="flex h-3 rounded-full overflow-hidden mb-1" aria-hidden="true">
            {view.buckets.map(
              (b) =>
                b.amt > 0 && (
                  <div
                    key={b.label}
                    style={{ width: `${(b.amt / Math.max(view.total, 1)) * 100}%`, background: b.color }}
                  />
                ),
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500 font-semibold mb-2">
            {view.buckets.map(
              (b) =>
                b.n > 0 && (
                  <span key={b.label} className={b.label === "60d+" ? "text-rose-700 font-extrabold" : ""}>
                    {b.label} · {fmtK(b.amt)} / {b.n}
                  </span>
                ),
            )}
          </div>

          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">
            Biggest balances
          </div>
          <div className="divide-y divide-slate-100">
            {view.top.map((r) => (
              <div key={r.id} className="py-1.5">
                <Link
                  to={`/deal/${r.id}`}
                  onClick={() => logCardClick("widget_ar_row")}
                  className="flex items-center gap-2"
                >
                  <span className="text-[13px] font-bold text-bardin-ink truncate flex-1">
                    {r.homeowner_name ?? "—"}
                  </span>
                  <span className="text-[12px] font-extrabold text-bardin-ink">
                    {fmtUSD0(Number(r.post_build_balance_remaining ?? 0))}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold ${
                      r.stage === "delinquent" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {r.stage === "delinquent" ? "DELINQ" : "Collecting"} · {ageDays(r)}d
                  </span>
                </Link>
                {commsMap && (
                  <CommsStrip
                    summary={commsMap[r.id] ?? null}
                    onOpen={() => {
                      setOpenId(r.id);
                      logCardClick("widget_ar_comms_open");
                    }}
                    className="mt-1"
                  />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/finance?tab=ar"
          onClick={() => logCardClick("widget_ar_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Open AR Aging ›
        </Link>
      </div>

      <DealCommsDrawer
        deal={
          openRow
            ? {
                id: openRow.id,
                homeowner_name: openRow.homeowner_name,
                homeowner_phone: openRow.homeowner_phone,
                homeowner_email: openRow.homeowner_email,
                claim_number: openRow.claim_number,
                stageLabel: openRow.stage === "delinquent" ? "Delinquent" : "Collecting $",
                stageDotClass: openRow.stage === "delinquent" ? "bg-red-500" : "bg-emerald-500",
                balance: openRow.post_build_balance_remaining != null ? Number(openRow.post_build_balance_remaining) : null,
              }
            : null
        }
        members={members ?? {}}
        onClose={closeComms}
        onSent={(dealId, m) =>
          setCommsMap((prev) => (prev ? withOptimisticOutbound(prev, dealId, m.body, m.created_at) : prev))
        }
      />
    </div>
  );
}
